import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../../Admin/Navbar/Navbar1";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { makeAuthenticatedGETRequest } from "../../services/serverHelper";
import {
  FaUsers,
  FaUserTie,
  FaHandshake,
  FaHeart,
  FaBriefcase,
  FaFileAlt,
  FaPlayCircle,
  FaRupeeSign,
} from "react-icons/fa";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import "./Dashboard.css";
import AdminSidebar from "../Navbar/AdminSidebar";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Dashboard() {
  const [totalArtists, setTotalArtists] = useState(0);
  const [totalPatrons, setTotalPatrons] = useState(0);
  const [totalPartners, setTotalPartners] = useState(0);
  const [totalArtlovers, setTotalArtlovers] = useState(0);
  const [totalOpportunities, setTotalOpportunities] = useState(0);
  const [totalApplications, setTotalApplications] = useState(0);
  const [totalperformances, setTotalPerformances] = useState(0);
  const [totalrevenue, setTotalRevenue] = useState(0);

  const token = localStorage.getItem("accessToken");
  const navigate = useNavigate();

  if (!token) {
    toast.error("Please login to access");
    navigate("/login");
  }

  useEffect(() => {
    const getUser = async (role) => {
      try {
        const response = await makeAuthenticatedGETRequest(
          `${BASE_URL}/admin/users?role=${role}`,
          token
        );

        if (response.status === "success") {
          if (role === "Artist") setTotalArtists(response.data.length);
          if (role === "Patron") setTotalPatrons(response.data.length);
          if (role === "Partner") setTotalPartners(response.data.length);
          if (role === "Art-lover") setTotalArtlovers(response.data.length);
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error(`Error fetching ${role} data:`, error);
      }
    };

    const getOpportunity = async () => {
      try {
        const response = await makeAuthenticatedGETRequest(
          `${BASE_URL}/admin/opps`,
          token
        );
        setTotalOpportunities(response.data.length);
      } catch (error) {
        console.error("Error fetching opportunity data:", error);
      }
    };

    const getApplications = async () => {
      try {
        const response = await makeAuthenticatedGETRequest(
          `${BASE_URL}/admin/allapps`,
          token
        );

        setTotalApplications(response.length);

        const hired = response.filter((a) => a.status === "Hired");
        setTotalPerformances(hired.length);

        let sum = 0;
        hired.forEach((elm) => {
          if (elm.opportunity) sum += elm.opportunity.budget;
        });
        setTotalRevenue(sum);
      } catch (error) {
        console.log("Error fetching application data:", error);
      }
    };

    getUser("Artist");
    getUser("Patron");
    getUser("Partner");
    getUser("Art-lover");
    getOpportunity();
    getApplications();
  }, []);

  const stats = [
    { title: "Patrons", value: totalPatrons, link: "/users?value=1", icon: <FaUserTie /> },
    { title: "Artists", value: totalArtists +30, link: "/users?value=0", icon: <FaUsers /> },
    { title: "Partners", value: totalPartners, link: "/users?value=2", icon: <FaHandshake /> },
    { title: "Art Lovers", value: totalArtlovers, link: "/users?value=3", icon: <FaHeart /> },
    { title: "Opportunities", value: totalOpportunities, link: "/Opportunity", icon: <FaBriefcase /> },
    { title: "Artist Days", value: 3000, link: "/OpportunityApplications", icon: <FaFileAlt /> },
    { title: "Performances", value: 450 + totalperformances, link: "/PerformanceEnquiries", icon: <FaPlayCircle /> },
    { title: "Revenue", value: `₹${totalrevenue}`, link: "/DashboardRevenue", icon: <FaRupeeSign />, highlight: true },
  ];

  // Data for Charts
  const userDistribution = [
    { name: "Artists", value: totalArtists },
    { name: "Patrons", value: totalPatrons },
    { name: "Partners", value: totalPartners },
    { name: "Art Lovers", value: totalArtlovers },
  ];
  const COLORS = ["#AD2F3B", "#FFB74D", "#4CAF50", "#42A5F5"];

  const applicationData = [
    { name: "Applications", count: totalApplications },
    { name: "Performances", count: totalperformances },
  ];

  const opportunityData = [
    { name: "Opportunities", count: totalOpportunities },
    { name: "Revenue", count: totalrevenue },
  ];

  return (
    <>
      <AdminNavbar />
      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="admin-dashboard-container">
          <h1 className="admin-dashboard-heading">Admin Dashboard</h1>

          {/* Stats Cards */}
          <div className="admin-stats-grid">
            {stats.map((stat, idx) => (
              <Link
                key={idx}
                to={stat.link}
                className={`admin-stat-card ${stat.highlight ? "highlight" : ""}`}
              >
                <div className="admin-stat-info">
                  <p className="admin-stat-title">{stat.title}</p>
                  <p className="admin-stat-value">{stat.value}</p>
                </div>
                <div className="admin-stat-icon">{stat.icon}</div>
              </Link>
            ))}
          </div>

          {/* Graphs Section */}
          <div className="admin-charts-grid">
            {/* User Distribution */}
            <div className="admin-chart-card">
              <h3>User Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart responsive>
                  <Pie
                    data={userDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    innerRadius={90}
                    outerRadius={120}
                    paddingAngle={2}
                    fill="#8884d8"
                    dataKey="value"
                    label={false}
                  >
                    <Legend layout="horizontal" align="bottom" height={36} />
                    {userDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Applications vs Performances */}
            <div className="admin-chart-card">
              <h3>Applications vs Performances</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={applicationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#AD2F3B" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Opportunities vs Revenue */}
            <div className="admin-chart-card">
              <h3>Opportunities vs Revenue</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={opportunityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#AD2F3B" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
